import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { Button } from '../../components/ui/Button.jsx';
import { Modal } from '../../components/ui/Modal.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { financeApi } from '../../services/financeApi.js';

const TYPES = ['ASSET', 'LIABILITY', 'EQUITY', 'REVENUE', 'EXPENSE'];
const EMPTY = { code: '', name: '', type: 'ASSET', parentId: '' };

export function AccountCreateModal({ open, onClose }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);

  const accounts = useQuery({
    queryKey: ['finance', 'accounts'],
    queryFn: () => financeApi.listAccounts().then((r) => r.items || []),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: (body) => financeApi.createAccount(body),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['finance', 'accounts'] });
      setForm(EMPTY);
      setError(null);
      onClose();
    },
    onError: (err) => setError(err?.message || t('common.loadError')),
  });

  const handle = (key) => (e) => setForm((p) => ({ ...p, [key]: e.target.value }));

  function submit(e) {
    e.preventDefault();
    setError(null);
    create.mutate({
      code: form.code.trim(),
      name: form.name.trim(),
      type: form.type,
      parentId: form.parentId || undefined,
    });
  }

  // Show only accounts of the same type as potential parents
  const eligibleParents = (accounts.data || []).filter((a) => a.type === form.type);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t('finance.accounts.modal.title')}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>{t('common.cancel')}</Button>
          <Button variant="primary" onClick={submit} disabled={create.isPending}>
            {create.isPending ? `${t('common.save')}…` : t('common.create')}
          </Button>
        </>
      }
    >
      <form onSubmit={submit} style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-3)' }}>
        <div className="field-row">
          <label className="field">
            <span className="field-label">{t('col.code')}</span>
            <input className="input" value={form.code} onChange={handle('code')} required />
          </label>
          <label className="field">
            <span className="field-label">{t('col.type')}</span>
            <select className="select" value={form.type} onChange={handle('type')}>
              {TYPES.map((tp) => (
                <option key={tp} value={tp}>{t(`accountType.${tp}`)}</option>
              ))}
            </select>
          </label>
        </div>
        <label className="field">
          <span className="field-label">{t('col.name')}</span>
          <input className="input" value={form.name} onChange={handle('name')} required />
        </label>
        <label className="field">
          <span className="field-label">{t('finance.accounts.modal.parent')}</span>
          <select className="select" value={form.parentId} onChange={handle('parentId')}>
            <option value="">{t('hr.employees.modal.optional')}</option>
            {eligibleParents.map((a) => (
              <option key={a.id} value={a.id}>{a.code} - {a.name}</option>
            ))}
          </select>
        </label>
        {error && <div className="alert danger">{error}</div>}
      </form>
    </Modal>
  );
}
